import { Link } from '..';
import { ReactRenderer } from '@tiptap/react';
import { BubbleMenuPluginProps, BubbleMenuView } from '@tiptap/extension-bubble-menu';
import { LinkEditorBubble } from './LinkEditorBubble';
import type { LinkEditorState } from './LinkEditorBubble';
import type { Placement } from 'tippy.js';
import type { EditorState } from '@tiptap/pm/state';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import type { EditorView } from '@tiptap/pm/view';
import debounce from 'lodash/debounce';

declare module '@tiptap/core' {
   interface Commands<ReturnType> {
      editableLink: {
         showLinkEditor: (state: LinkEditorState) => ReturnType;
         hideLinkEditor: () => ReturnType;
         editLink: () => ReturnType;
         forceShow: () => ReturnType;
      };
   }
}

class ClickSelectBubbleMenuView extends BubbleMenuView {
   update(view: EditorView, oldState?: EditorState) {
      const hasValidSelection = true;

      if (this.updateDelay > 0 && hasValidSelection) {
         debounce(this.updateHandler, this.updateDelay)(view, oldState);
      } else {
         this.updateHandler(view, oldState);
      }
   }
}

export const ClickSelectBubbleMenuPlugin = (options: BubbleMenuPluginProps) => {
   return new Plugin({
      key:
         typeof options.pluginKey === 'string'
            ? new PluginKey(options.pluginKey)
            : options.pluginKey,
      view: view => new ClickSelectBubbleMenuView({ view, ...options }),
   });
};

export const EditableLink = Link.extend({
   inclusive: false,
   priority: -10,

   onCreate() {
      let props = { editor: this.editor };
      const duration: [number, number] = [100, 0];
      const placement: Placement = 'bottom';

      const tippyOptions = {
         duration,
         maxWidth: 'none',
         moveTransition: 'transform 0.1s ease 0.05s',
         ignoreAttributes: true,
         placement,
      };

      setTimeout(() => {
         const editorid = this.editor.storage.editorid.editorid;
         this.storage[`${editorid}-renderer`] = new ReactRenderer(LinkEditorBubble, {
            props,
            editor: this.editor,
         });

         this.storage.bubbleMenu = ClickSelectBubbleMenuPlugin({
            pluginKey: `bubble-menu-${editorid}`,
            editor: this.editor,
            element: this.storage[`${editorid}-renderer`].element,
            shouldShow: () => this.editor.isActive('link') || this.storage.forceShow,
            tippyOptions: tippyOptions,
            updateDelay: 50,
         });

         this.editor.registerPlugin(this.storage.bubbleMenu);
      }, 250);
   },
   addCommands() {
      return {
         ...this.parent?.(),
         showLinkEditor:
            (state?: LinkEditorState) =>
            ({ dispatch }) => {
               const editorid = this.editor.storage.editorid.editorid;
               const renderer = this.storage[`${editorid}-renderer`];
               if (dispatch && renderer) {
                  renderer.ref.show(state);
               }
               return true;
            },
         hideLinkEditor:
            () =>
            ({ dispatch }) => {
               const editorid = this.editor.storage.editorid.editorid;
               const renderer = this.storage[`${editorid}-renderer`];
               if (dispatch && renderer) {
                  renderer.ref.hide();
               }
               return true;
            },
         editLink:
            () =>
            ({ dispatch, commands, editor }) => {
               if (!dispatch) {
                  return true;
               }
               commands.extendMarkRange('link');
               if (!editor.isActive('link')) {
                  commands.forceShow();
               }
               commands.showLinkEditor('edit');
               return true;
            },
         forceShow:
            () =>
            ({ dispatch, editor }) => {
               if (!dispatch) {
                  return true;
               }
               this.storage.forceShow = true;
               // For some reason, this is needed to force the bubble to show
               editor.commands.blur();
               // The blur command uses requestAnimationFrame, so we need to wait for that to finish before refocusing.
               requestAnimationFrame(() => {
                  editor.commands.focus();
               });
               return true;
            },
      };
   },
   addStorage() {
      return {
         renderer: null,
         bubbleMenu: null,
         forceShow: false,
      };
   },
   addKeyboardShortcuts() {
      return {
         'Mod-k': () => this.editor.commands.editLink(),
      };
   },
   onSelectionUpdate() {
      this.storage.forceShow = false;
      if (this.editor.isActive('link')) {
         this.editor.commands.showLinkEditor('preview');
      } else {
         this.editor.commands.hideLinkEditor();
      }
   },
   onExit() {
      if (this.storage.renderer) {
         this.storage.renderer.destroy();
      }
      this.editor.unregisterPlugin(this.storage.bubbleMenu);
   },
   addPasteRules() {
      return [];
   },
}).configure({ openOnClick: false, linkOnPaste: false, autolink: false });
